import {
  PriceBookBranchScope,
  PriceBookStatus,
  PricebookChangeSummary,
  PricebookCreationValidity,
  PricebookHeaderChanges,
  PricebookHistoryAction,
  PricebookHistoryEntry,
  PricebookLineChangeCounts,
  PricebookPeriodChanges,
  PricebookPeriodHistoryDetail,
  PricebookValueChange,
} from "../../../../types/price-book.type";
import { formatNumber, numberWithCommas } from "../../../../utils";
import { STATUS_LABEL } from "../../shared/constants";
import {
  ALL_BRANCHES_LABEL,
  PRICE_BOOK_TYPE_LABEL,
  UNKNOWN_PRICE_BOOK_TYPE_LABEL,
  formatDiscountPercent,
  toPriceBookType,
} from "../constants";
import { formatPeriodDate, formatPeriodRange } from "../Periods/periodModel";
import { SCHEDULE_CONFLICT_TEXT } from "../Schedules/scheduleModel";
import { formatValidityText } from "../validityModel";

const SEPARATOR = " · ";
const NO_VALUE = "-";

export const HISTORY_ACTION_LABEL: Record<PricebookHistoryAction, string> = {
  created: "สร้างสมุดราคา",
  duplicated: "ทำสำเนาเป็นเล่มใหม่",
  draft_created: "ทำสำเนาไปทดสอบ (ร่าง)",
  updated: "แก้ไขข้อมูลสมุด",
  items_replaced: "บันทึกรายการและราคาสินค้า",
  branches_updated: "เปลี่ยนสาขาที่ใช้",
  face_price_updated: "ตั้งราคาหน้าซอง",
  face_price_applied: "อัปเดตตามราคาหน้าซอง",
  product_removed: "สินค้าถูกลบ",
  draft_applied: "นำร่างมาใช้",
  deleted: "ลบสมุดราคา",
  discount_migrated: "ย้ายเป็นส่วนลดสมุด",
  period_created: "เพิ่มช่วงราคา",
  period_updated: "แก้ไขช่วงราคา",
  period_extended: "ต่อช่วงราคา",
  period_deleted: "ลบช่วงราคา",
  draft_range_applied: "นำร่างมาใช้เป็นช่วงราคา",
  schedule_created: "ตั้งเวลานำร่างมาใช้",
  schedule_cancelled: "ยกเลิกการตั้งเวลา",
  schedule_applied: "นำร่างมาใช้ตามเวลาที่ตั้ง",
  schedule_conflict: "ไม่นำร่างมาใช้ (ชนกัน)",
};

/** Shown where a period keeps its line's unit price or %. */
const FOLLOWS_LINE_LABEL = "ตามรายการ";

/** Shown where a validity day has no bound. */
const NO_BOUND_LABEL = "ไม่กำหนด";

/** Shown as who made an entry a script wrote. */
export const SCRIPT_ACTOR_LABEL = "ระบบ (สคริปต์)";

const BRANCH_SCOPE_LABEL: Record<PriceBookBranchScope, string> = {
  all: ALL_BRANCHES_LABEL,
  selected: "เลือกสาขา",
};

const formatChange = (
  label: string,
  change: PricebookValueChange<string>,
): string => `${label} ${change.old} → ${change.new}`;

const mapChange = <Value>(
  change: PricebookValueChange<Value>,
  format: (value: Value) => string,
): PricebookValueChange<string> => ({
  old: format(change.old),
  new: format(change.new),
});

const formatTypeLabel = (type: string | null): string => {
  const knownType = toPriceBookType(type);
  return knownType ? PRICE_BOOK_TYPE_LABEL[knownType] : UNKNOWN_PRICE_BOOK_TYPE_LABEL;
};

const formatStatusLabel = (status: PriceBookStatus | null): string =>
  status ? STATUS_LABEL[status] : NO_VALUE;

const formatValidityDay = (day: string | null): string =>
  day ? formatPeriodDate(day) : NO_BOUND_LABEL;

const describeHeaderChanges = (changes: PricebookHeaderChanges): string => {
  const parts: string[] = [];
  if (changes.name) {
    parts.push(
      formatChange("ชื่อ", mapChange(changes.name, (name) => name ?? NO_VALUE)),
    );
  }
  if (changes.type) {
    parts.push(formatChange("ประเภท", mapChange(changes.type, formatTypeLabel)));
  }
  if (changes.status) {
    parts.push(formatChange("สถานะ", mapChange(changes.status, formatStatusLabel)));
  }
  if (changes.discount_percent) {
    parts.push(
      formatChange(
        "ส่วนลดสมุด",
        mapChange(changes.discount_percent, formatDiscountPercent),
      ),
    );
  }
  if (changes.effective_from) {
    parts.push(
      formatChange("วันเริ่มใช้", mapChange(changes.effective_from, formatValidityDay)),
    );
  }
  if (changes.effective_to) {
    parts.push(
      formatChange("วันสิ้นสุด", mapChange(changes.effective_to, formatValidityDay)),
    );
  }
  return parts.join(SEPARATOR) || "ไม่มีค่าที่เปลี่ยน";
};

/** The non-zero added / taken off / changed counts. */
const describeChangeCounts = (summary: PricebookChangeSummary): string[] =>
  [
    { label: "เพิ่ม", count: summary.added_count },
    { label: "นำออก", count: summary.removed_count },
    { label: "เปลี่ยน", count: summary.changed_count },
  ]
    .filter(({ count }) => count > 0)
    .map(({ label, count }) => `${label} ${numberWithCommas(count)}`);

/** The non-zero counts, then how many lines the book holds after the write. */
const describeLineCounts = (counts: PricebookLineChangeCounts): string =>
  [
    ...describeChangeCounts(counts),
    `รวม ${numberWithCommas(counts.item_count)} รายการ`,
  ].join(SEPARATOR);

/** The days a new book or a copy was created valid on; nothing for one valid every day. */
const describeCreationValidity = (validity: PricebookCreationValidity): string[] => {
  const text = formatValidityText({
    effective_from: validity.effective_from ?? null,
    effective_to: validity.effective_to ?? null,
  });
  return text ? [text] : [];
};

const describeBookDiscountChange = (
  change: PricebookValueChange<number>,
): string[] =>
  change.old === change.new
    ? []
    : [formatChange("ส่วนลดสมุด", mapChange(change, formatDiscountPercent))];

const describeMovedShops = (movedShopCount: number): string[] =>
  movedShopCount > 0
    ? [`ร้านทดสอบ ${numberWithCommas(movedShopCount)} ร้านกลับมาใช้สมุดนี้`]
    : [];

const describeBranchChange = (
  detail: Extract<PricebookHistoryEntry, { action: "branches_updated" }>["detail"],
  branchCodeById: ReadonlyMap<string, string>,
): string => {
  const toCodes = (companyIds: string[]) =>
    companyIds.map((companyId) => branchCodeById.get(companyId) ?? companyId).join(", ");
  const parts: string[] = [];
  if (detail.branch_scope.old !== detail.branch_scope.new) {
    parts.push(
      `${BRANCH_SCOPE_LABEL[detail.branch_scope.old]} → ${BRANCH_SCOPE_LABEL[detail.branch_scope.new]}`,
    );
  }
  if (detail.added_company_ids.length > 0) {
    parts.push(`เปิด ${toCodes(detail.added_company_ids)}`);
  }
  if (detail.removed_company_ids.length > 0) {
    parts.push(`ปิด ${toCodes(detail.removed_company_ids)}`);
  }
  return parts.join(SEPARATOR) || "ไม่มีสาขาที่เปลี่ยน";
};

const describeAppliedDraft = (
  detail: Extract<PricebookHistoryEntry, { action: "draft_applied" }>["detail"],
): string =>
  [
    `จากร่าง ${detail.draft_code}`,
    ...describeBookDiscountChange(detail.discount_percent),
    describeLineCounts(detail),
    ...describeMovedShops(detail.moved_shop_count),
  ].join(SEPARATOR);

const describeRangeApplied = (
  detail: Extract<PricebookHistoryEntry, { action: "draft_range_applied" }>["detail"],
): string =>
  [
    `จากร่าง ${detail.draft_code}`,
    `ช่วง ${formatPeriodRange(detail.start_date, detail.end_date)}`,
    `ช่วงราคา ${numberWithCommas(detail.periods.length)} รายการ`,
    ...describeMovedShops(detail.moved_shop_count),
  ].join(SEPARATOR);

const describeScheduleCreated = (
  detail: Extract<PricebookHistoryEntry, { action: "schedule_created" }>["detail"],
): string =>
  [
    `จากร่าง ${detail.draft_code}`,
    `มีผล ${formatPeriodDate(detail.effective_date)}`,
    ...describeBookDiscountChange(detail.discount_percent),
    ...describeChangeCounts(detail.summary),
    ...describeMovedShops(detail.moved_shop_count),
  ].join(SEPARATOR);

const describeScheduleApplied = (
  detail: Extract<PricebookHistoryEntry, { action: "schedule_applied" }>["detail"],
): string =>
  [
    `จากร่าง ${detail.draft_code}`,
    `มีผล ${formatPeriodDate(detail.effective_date)}`,
    ...describeBookDiscountChange(detail.discount_percent),
    describeLineCounts(detail),
  ].join(SEPARATOR);

const describePeriod = (detail: PricebookPeriodHistoryDetail): string =>
  [
    `${detail.product_code} ${formatPeriodRange(detail.start_date, detail.end_date)}`,
    `รวม VAT ${formatNumber(detail.price_incl_vat)}`,
    `ก่อน VAT ${formatNumber(detail.price_ex_vat)}`,
  ].join(SEPARATOR);

const describePeriodChanges = (
  productCode: string,
  changes: PricebookPeriodChanges,
): string => {
  const formatOwnPrice = (value: number | null) =>
    value === null ? FOLLOWS_LINE_LABEL : formatNumber(value);
  const formatOwnPercent = (value: number | null) =>
    value === null ? FOLLOWS_LINE_LABEL : formatDiscountPercent(value);
  const parts = [productCode];
  if (changes.start_date) {
    parts.push(formatChange("วันเริ่ม", mapChange(changes.start_date, formatPeriodDate)));
  }
  if (changes.end_date) {
    parts.push(formatChange("วันสิ้นสุด", mapChange(changes.end_date, formatPeriodDate)));
  }
  if (changes.unit_price) {
    parts.push(formatChange("ราคาต่อหน่วย", mapChange(changes.unit_price, formatOwnPrice)));
  }
  if (changes.discount_percent) {
    parts.push(
      formatChange("ส่วนลด", mapChange(changes.discount_percent, formatOwnPercent)),
    );
  }
  if (changes.price_incl_vat) {
    parts.push(formatChange("รวม VAT", mapChange(changes.price_incl_vat, formatNumber)));
  }
  if (changes.note) {
    parts.push(
      formatChange("หมายเหตุ", mapChange(changes.note, (note) => note ?? NO_VALUE)),
    );
  }
  return parts.join(SEPARATOR);
};

/**
 * One history entry in Thai. `branchCodeById` turns the company ids a branch change records
 * into branch codes; an id it does not know is shown as it is.
 */
export const describeHistoryEntry = (
  entry: PricebookHistoryEntry,
  branchCodeById: ReadonlyMap<string, string>,
): string => {
  switch (entry.action) {
    case "created":
      return [
        `ส่วนลดสมุด ${formatDiscountPercent(entry.detail.discount_percent)}`,
        `${numberWithCommas(entry.detail.item_count)} รายการ`,
        ...describeCreationValidity(entry.detail),
      ].join(SEPARATOR);
    case "duplicated":
    case "draft_created":
      return [
        `จาก ${entry.detail.source_pricebook_code}`,
        `ส่วนลดสมุด ${formatDiscountPercent(entry.detail.discount_percent)}`,
        `${numberWithCommas(entry.detail.item_count)} รายการ`,
        ...describeCreationValidity(entry.detail),
      ].join(SEPARATOR);
    case "updated":
      return describeHeaderChanges(entry.detail.changes);
    case "items_replaced":
      return describeLineCounts(entry.detail);
    case "branches_updated":
      return describeBranchChange(entry.detail, branchCodeById);
    case "face_price_updated":
      return `${entry.detail.product_code} ราคาหน้าซอง ${formatNumber(entry.detail.unit_price)}`;
    case "face_price_applied":
      return `${entry.detail.product_code} ราคาต่อหน่วย ${formatNumber(
        entry.detail.old_unit_price,
        { fallback: NO_VALUE },
      )} → ${formatNumber(entry.detail.unit_price)}`;
    case "product_removed":
      return `${entry.detail.product_code} ถูกลบจากรายการสินค้า`;
    case "draft_applied":
      return describeAppliedDraft(entry.detail);
    case "deleted":
      return NO_VALUE;
    case "discount_migrated":
      return `ส่วนลดสมุด ${formatDiscountPercent(entry.detail.discount_percent)}`;
    case "period_created":
    case "period_deleted":
      return describePeriod(entry.detail);
    case "period_updated":
      return describePeriodChanges(entry.detail.product_code, entry.detail.changes);
    case "period_extended":
      return `${entry.detail.product_code} ${formatChange(
        "วันสิ้นสุด",
        mapChange(entry.detail.end_date, formatPeriodDate),
      )}`;
    case "draft_range_applied":
      return describeRangeApplied(entry.detail);
    case "schedule_created":
      return describeScheduleCreated(entry.detail);
    case "schedule_cancelled":
      return `การตั้งเวลาที่มีผล ${formatPeriodDate(entry.detail.effective_date)}`;
    case "schedule_applied":
      return describeScheduleApplied(entry.detail);
    case "schedule_conflict":
      return `การตั้งเวลาที่มีผล ${formatPeriodDate(entry.detail.effective_date)}${SEPARATOR}${
        SCHEDULE_CONFLICT_TEXT[entry.detail.reason]
      }`;
  }
};
